/**
 * RulesView — the rulebook as players read it: sections of plain sentences,
 * what changed since they registered, search, share and PDF.
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { rulebookApi } from './api';
import { KIND_META, SECTION_ACCENT } from './ruleText';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export function ChangeBadge({ changed }) {
  if (!changed) return null;
  const styles = {
    added: 'bg-[#E6F5F4] text-[#1F7A75]',
    changed: 'bg-[#FCE8EC] text-[#B3263F]',
    removed: 'bg-gray-100 text-gray-500',
  };
  const words = { added: 'New', changed: 'Changed', removed: 'Removed' };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${styles[changed]}`}>{words[changed]}</span>;
}

export default function RulesView({ event, data, catalogue, preview = false }) {
  const [query, setQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const rules = useMemo(() => data.rules || [], [data.rules]);
  const live = data.live;
  const changedCount = rules.filter((r) => r.changed).length;

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (catalogue?.sections || []).map((s) => ({
      ...s,
      rules: rules.filter((r) => r.section === s.key && (!q || r.sentence.toLowerCase().includes(q) || (r.source_text || '').toLowerCase().includes(q))),
    })).filter((s) => s.rules.length);
  }, [catalogue, rules, query]);

  if (!live) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="rules-not-published">
        <i className="ph-duotone ph-book-open text-5xl text-[#3AAFA9]" />
        <p className="font-extrabold text-lg mt-2">Rules coming soon</p>
        <p className="text-sm text-gray-500">The organiser hasn't published the rulebook yet.</p>
      </div>
    );
  }

  const share = async () => {
    const link = `${window.location.origin}/tournaments/${event.id}?phase=rules`;
    const text = `${event.title} — rules: ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${event.title} rules`, text, url: link });
        return;
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };
  const pdf = () => window.open(rulebookApi.pdfUrl(event.id), '_blank', 'noopener');
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/tournaments/${event.id}?phase=rules`);
      toast.success('Link copied');
    } catch { /* clipboard blocked: share instead */ share(); }
  };

  return (
    <div className="space-y-3 lg:max-w-3xl" data-testid="rules-view">
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-extrabold text-lg leading-tight">{preview ? 'What players see' : 'Rulebook'}</p>
            <p className="text-xs text-gray-500">
              Version {live.number} · updated {fmtDate(live.published_at)}
              {live.applies_from_match ? ` · from match ${live.applies_from_match}` : ''}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={share} className="w-10 h-10 rounded-full bg-[#E6F5F4] text-[#1F7A75]" aria-label="Share rules" data-testid="rules-share">
              <i className="ph-bold ph-share-network" />
            </button>
            <button onClick={pdf} className="w-10 h-10 rounded-full bg-[#E9EBF1] text-[#2B3A67]" aria-label="Rules as PDF" data-testid="rules-pdf">
              <i className="ph-bold ph-file-pdf" />
            </button>
            <button onClick={copyLink} className="hidden sm:inline-flex w-10 h-10 rounded-full bg-[#E9EBF1] text-[#2B3A67] items-center justify-center" aria-label="Copy link">
              <i className="ph-bold ph-link" />
            </button>
          </div>
        </div>
        {live.change_summary && <p className="text-sm text-gray-600 mt-2"><span className="font-bold">Latest change:</span> {live.change_summary}</p>}
        {changedCount > 0 && data.my_version && (
          <div className="mt-3 rounded-xl bg-[#FDF5D3] border border-[#F4D341] px-3 py-2 text-sm text-[#2B3A67]" data-testid="rules-changed-banner">
            <i className="ph-bold ph-bell-ringing" /> {changedCount} rule{changedCount > 1 ? 's' : ''} changed since you registered (version {data.my_version}).
          </div>
        )}
        <div className="mt-3 relative">
          <i className="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the rules"
            className="w-full h-11 pl-9 pr-3 rounded-full bg-[#F5F7FA] border border-gray-100 text-sm" />
        </div>
      </div>

      {sections.length === 0 && <p className="text-sm text-gray-500 text-center py-6">No rules match "{query}".</p>}
      {sections.map((s) => (
        <div key={s.key} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
          style={{ borderLeft: `4px solid ${SECTION_ACCENT[s.key]}` }}>
          <p className="px-4 pt-3 pb-1 font-extrabold flex items-center gap-2"><i className={`ph-bold ${s.icon}`} />{s.label}</p>
          <ul className="divide-y divide-gray-100">
            {s.rules.map((r) => (
              <li key={r.id} className="px-4 py-3 text-[15px] leading-snug" data-testid="rules-view-rule">
                <div className="flex items-start gap-2">
                  <span className="flex-1">{r.block === 'text' ? r.source_text : r.sentence}</span>
                  <ChangeBadge changed={r.changed} />
                </div>
                {r.block !== 'text' && r.source_text && <p className="text-xs text-gray-500 mt-1">“{r.source_text}”</p>}
                {r.kind === 'umpire' && <p className="text-[11px] mt-1" style={{ color: KIND_META.umpire.color }}><i className="ph-bold ph-gavel" /> Umpire decides</p>}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {(data.versions || []).length > 1 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <button className="w-full flex justify-between items-center font-bold text-sm" onClick={() => setShowHistory((v) => !v)}>
            <span><i className="ph-bold ph-clock-counter-clockwise" /> Earlier versions</span>
            <i className={`ph-bold ${showHistory ? 'ph-caret-up' : 'ph-caret-down'}`} />
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-2">
              {data.versions.map((v) => (
                <li key={v.number} className="flex items-center justify-between text-sm">
                  <span>Version {v.number} · {fmtDate(v.published_at)}{v.change_summary ? ` — ${v.change_summary}` : ''}</span>
                  <a className="text-[#1F7A75] font-semibold" href={rulebookApi.pdfUrl(event.id, v.number)} target="_blank" rel="noopener noreferrer">PDF</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
